/**
 * PCISPH (Predictive-Corrective Incompressible SPH) Solver Implementation
 *
 * Implements incompressibility through iterative prediction-correction:
 * - Predicts particle positions without pressure
 * - Iteratively corrects pressure to enforce density constraint
 * - More stable than explicit pressure methods
 * - Typically 3-5 iterations per time step
 *
 * Key Features:
 * - Predictive velocity and position calculation
 * - Iterative pressure correction
 * - Density error minimization
 * - Self-contained integration
 *
 * Pipeline Stages:
 * 1. Compute density
 * 2. Apply non-pressure forces (viscosity, surface tension)
 * 3. Predict velocity and position
 * 4. Pressure iteration (3 iterations default)
 * 5. Final integration with corrected pressure
 *
 * Based on "Predictive-Corrective Incompressible SPH" (Solenthaler & Pajarola, SIGGRAPH 2009)
 *
 * @see ISPHSolver
 */

import type { ISPHSolver, GPUSolverBuffers } from './ISPHSolver';
import type { PCISPHParametreler } from '../FikoSPHParametreler';
import {
  PCISPH_COMPUTE_DENSITY,
  PCISPH_NON_PRESSURE_FORCES,
  PCISPH_PREDICT_VEL_POS,
  PCISPH_PRESSURE_ITERATION,
  PCISPH_FINAL_INTEGRATE,
  PCISPH_SOLVER_BINDINGS
} from './sph-pcisph-shaders.wgsl';

/**
 * FikoPCISPHSolver - GPU-accelerated PCISPH implementation
 *
 * Uses 5 compute pipelines for the PCISPH algorithm:
 * - Density computation
 * - Non-pressure forces
 * - Velocity/position prediction
 * - Pressure iteration (repeated)
 * - Final integration
 */
export class FikoPCISPHSolver implements ISPHSolver {
  readonly ad = 'PCISPH';
  readonly passSayisi = 7; // 1 density, 1 nonPressure, 1 predict, 3 pressureIter, 1 finalIntegrate
  readonly kendiIntegrasyonuVar = true; // PCISPH handles integration internally

  private device: GPUDevice | null = null;
  private maxParticle: number = 0;

  // Pipeline infrastructure
  private pipelines: Map<string, GPUComputePipeline> = new Map();

  // Solver-specific buffers
  private solverBuffersRef: GPUSolverBuffers | null = null;
  private currentBindGroup: GPUBindGroup | null = null;

  // Solver parameters
  private params: PCISPHParametreler = {
    pcisphIter: 3,
    pcisphMinIter: 2,
    pcisphMaxError: 0.01
  };

  /**
   * Initialize the solver with GPU device and constraints
   */
  async basla(
    device: GPUDevice,
    maxParticle: number,
    ortakBindGroupLayout: GPUBindGroupLayout
  ): Promise<void> {
    this.device = device;
    this.maxParticle = maxParticle;

    // Create 5 compute pipelines with auto layout
    await this.createPipelines();
  }

  /**
   * Create all 5 PCISPH compute pipelines
   */
  private async createPipelines(): Promise<void> {
    if (!this.device) {
      throw new Error('Device not initialized');
    }

    const pipelineConfigs = [
      { name: 'computeDensity', shader: PCISPH_COMPUTE_DENSITY, label: 'PCISPH Compute Density' },
      { name: 'nonPressureForces', shader: PCISPH_NON_PRESSURE_FORCES, label: 'PCISPH Non-Pressure Forces' },
      { name: 'predictVelPos', shader: PCISPH_PREDICT_VEL_POS, label: 'PCISPH Predict Velocity+Position' },
      { name: 'pressureIteration', shader: PCISPH_PRESSURE_ITERATION, label: 'PCISPH Pressure Iteration' },
      { name: 'finalIntegrate', shader: PCISPH_FINAL_INTEGRATE, label: 'PCISPH Final Integration' }
    ];

    for (const config of pipelineConfigs) {
      const shaderModule = this.device.createShaderModule({
        label: `${config.label} Shader`,
        code: config.shader
      });

      const pipeline = this.device.createComputePipeline({
        label: config.label,
        layout: 'auto',
        compute: {
          module: shaderModule,
          entryPoint: 'main'
        }
      });

      this.pipelines.set(config.name, pipeline);
    }
  }

  /**
   * Create solver-specific GPU buffers
   *
   * PCISPH requires:
   * - pressure: Current pressure per particle
   * - pressureForce: Accumulated pressure force
   * - predictedPos: Predicted particle position
   * - predictedDens: Predicted density at predicted position
   */
  olusturBufferlar(device: GPUDevice, maxParticle: number): GPUSolverBuffers {
    const pressureBuffer = device.createBuffer({
      label: 'PCISPH Pressure Buffer',
      size: maxParticle * 4, // f32 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const pressureForceBuffer = device.createBuffer({
      label: 'PCISPH Pressure Force Buffer',
      size: maxParticle * 16, // vec4 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const predictedPosBuffer = device.createBuffer({
      label: 'PCISPH Predicted Position Buffer',
      size: maxParticle * 16, // vec4 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const predictedDensBuffer = device.createBuffer({
      label: 'PCISPH Predicted Density Buffer',
      size: maxParticle * 4, // f32 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const buffers = new Map<string, GPUBuffer>([
      ['pressure', pressureBuffer],
      ['pressureForce', pressureForceBuffer],
      ['predictedPos', predictedPosBuffer],
      ['predictedDens', predictedDensBuffer]
    ]);

    return {
      buffers,
      totalBytes: maxParticle * 40, // 4 + 16 + 16 + 4 = 40 bytes per particle
      dispose: () => {
        pressureBuffer.destroy();
        pressureForceBuffer.destroy();
        predictedPosBuffer.destroy();
        predictedDensBuffer.destroy();
      }
    };
  }

  /**
   * Get the bind group layout for solver buffers
   *
   * Returns layout from first pipeline (all pipelines share same @group(3) layout)
   */
  solverBindGroupLayout(): GPUBindGroupLayout {
    const pipeline = this.pipelines.get('computeDensity');
    if (!pipeline) {
      throw new Error('Pipelines not initialized');
    }
    return pipeline.getBindGroupLayout(3);
  }

  /**
   * Create bind group for solver buffers
   *
   * @group(3) bindings:
   * - @binding(0): pressure buffer (storage, read_write)
   * - @binding(1): pressureForce buffer (storage, read_write)
   * - @binding(2): predictedPos buffer (storage, read_write)
   * - @binding(3): predictedDens buffer (storage, read_write)
   */
  olusturBindGroup(device: GPUDevice, buffers: GPUSolverBuffers): GPUBindGroup {
    const layout = this.solverBindGroupLayout();

    const pressureBuffer = buffers.buffers.get('pressure');
    const pressureForceBuffer = buffers.buffers.get('pressureForce');
    const predictedPosBuffer = buffers.buffers.get('predictedPos');
    const predictedDensBuffer = buffers.buffers.get('predictedDens');

    if (!pressureBuffer || !pressureForceBuffer || !predictedPosBuffer || !predictedDensBuffer) {
      throw new Error('Required PCISPH buffers not found');
    }

    this.currentBindGroup = device.createBindGroup({
      label: 'PCISPH Solver Bind Group',
      layout,
      entries: [
        {
          binding: 0,
          resource: { buffer: pressureBuffer }
        },
        {
          binding: 1,
          resource: { buffer: pressureForceBuffer }
        },
        {
          binding: 2,
          resource: { buffer: predictedPosBuffer }
        },
        {
          binding: 3,
          resource: { buffer: predictedDensBuffer }
        }
      ]
    });

    this.solverBuffersRef = buffers;
    return this.currentBindGroup;
  }

  /**
   * Encode all PCISPH solver compute passes
   *
   * Execution order:
   * 1. Compute density
   * 2. Apply non-pressure forces (viscosity, surface tension)
   * 3. Predict velocity and position without pressure
   * 4. Pressure iteration loop (default 3 iterations)
   * 5. Final integration with corrected pressure
   */
  encodeSolverPasslari(
    encoder: GPUCommandEncoder,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    // Pass 1: Compute density
    this.encodePass(
      encoder,
      'computeDensity',
      'PCISPH Compute Density',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Pass 2: Non-pressure forces (viscosity, surface tension)
    this.encodePass(
      encoder,
      'nonPressureForces',
      'PCISPH NonPressure Forces',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Pass 3: Predict velocity and position
    this.encodePass(
      encoder,
      'predictVelPos',
      'PCISPH Predict VelPos',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Pressure iteration loop
    for (let i = 0; i < this.params.pcisphIter; i++) {
      this.encodePass(
        encoder,
        'pressureIteration',
        `PCISPH Pressure Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );
    }

    // Pass 5: Final integration
    this.encodePass(
      encoder,
      'finalIntegrate',
      'PCISPH Final Integrate',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );
  }

  /**
   * Encode a single compute pass with proper bind group setup
   */
  private encodePass(
    encoder: GPUCommandEncoder,
    pipelineName: string,
    label: string,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    const pipeline = this.pipelines.get(pipelineName);
    if (!pipeline) {
      console.warn(`PCISPH pipeline '${pipelineName}' not found`);
      return;
    }

    const pass = encoder.beginComputePass({ label });
    pass.setPipeline(pipeline);

    // Bind groups:
    // @group(0): Uniform parameters (ortak)
    // @group(1): Particle data (positions, velocities, etc.)
    // @group(2): Grid data (spatial hashing)
    // @group(3): Solver-specific data (pressure, pressureForce, predictedPos, predictedDens)
    pass.setBindGroup(0, ortakBindGroup);
    pass.setBindGroup(1, particleBindGroup);
    pass.setBindGroup(2, gridBindGroup);
    pass.setBindGroup(3, solverBindGroup);

    pass.dispatchWorkgroups(workgroupCount);
    pass.end();
  }

  /**
   * Update solver parameters at runtime
   */
  guncelleParametreler(params: PCISPHParametreler): void {
    this.params = { ...this.params, ...params };

    // Recalculate total pass count
    const newPassCount = 4 + this.params.pcisphIter;
    (this as any).passSayisi = newPassCount;
  }

  /**
   * Cleanup GPU resources
   */
  temizle(): void {
    // Dispose pipelines
    this.pipelines.clear();

    // Dispose solver buffers if we created them
    if (this.solverBuffersRef) {
      this.solverBuffersRef.dispose();
      this.solverBuffersRef = null;
    }

    this.currentBindGroup = null;
    this.device = null;
  }

  /**
   * Get all shader source code for debugging/inspection
   */
  shaderKaynagi(): string {
    return [
      '// ===== PCISPH Solver Bindings =====',
      PCISPH_SOLVER_BINDINGS,
      '',
      '// ===== 1. Compute Density =====',
      PCISPH_COMPUTE_DENSITY,
      '',
      '// ===== 2. Non-Pressure Forces =====',
      PCISPH_NON_PRESSURE_FORCES,
      '',
      '// ===== 3. Predict Velocity + Position =====',
      PCISPH_PREDICT_VEL_POS,
      '',
      '// ===== 4. Pressure Iteration =====',
      PCISPH_PRESSURE_ITERATION,
      '',
      '// ===== 5. Final Integration =====',
      PCISPH_FINAL_INTEGRATE
    ].join('\n');
  }
}

// Export both as default and named export
export default FikoPCISPHSolver;
